import {
  Box,
  Card,
  CardContent,
  LinearProgress,
  List,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import EngineeringOutlined from "@mui/icons-material/EngineeringOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import {
  GetNetSalesTechnicianResponse,
  NetSalesTechnicianResponse,
} from "subModule/src/services/analytics/interface";
import { useGetNetSalesTechniciansService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
interface NetSalesTechniciansProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function NetSalesTechnicians(props: NetSalesTechniciansProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const listColors = useMemo(
    () => [
      props.themes?.primary || "#4F46E5",
      props.themes?.secondary || "#EA580C",
      "#1CACDC",
      "#EF212C",
      "#10B981",
      "#8B5CF6",
      "#F59E0B",
      "#EC4899",
    ],
    [props.themes?.primary, props.themes?.secondary],
  );
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetNetSalesTechniciansService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetNetSalesTechnicianResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  const maxSale = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return 1;
    return Math.max(...data.map((d: any) => Number(d.saleAmount) || 0), 1);
  }, [data]);
  const getInitials = (email?: string) => {
    if (!email) return "?";
    const name = email.split("@")[0] || email;
    const parts = name.split(/[._-]/).filter(Boolean);
    if (parts.length >= 2)
      return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  };
  return (
    <Card
      sx={{
        height: "270px",
        marginTop: "16px",
        boxShadow: "none",
        borderRadius: "8px",
        "& .MuiCardContent-root": { padding: "16px !important" },
      }}
    >
      <CardContent>
        <Box sx={dashboardWidgetHeaderSx}>
          <AnalyticsWidgetHeader
            title={t("technicianSalesSummary")}
            icon={<EngineeringOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "technicianSales"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "technicianSales",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "technicianSales",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions(
                          "technicianSales",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        {loading ? (
          <>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </>
        ) : (
          <List dense className="h-[200px] overflow-y-auto p-0" sx={{ mt: 1.5 }}>
            {(data && data?.length === 0) || !data ? (
              <Box className="flex justify-center flex-col">
                <Typography
                  className="font-Saira text-center italic font-semibold mt-[40px]"
                  sx={{ color: props.themes.primary }}
                >
                  {t("noRecordFound")}
                </Typography>
              </Box>
            ) : (
              Array.isArray(data) &&
              [...data]
                ?.sort((a, b) => b.saleAmount - a.saleAmount)
                ?.map((item: NetSalesTechnicianResponse, index: number) => {
                  const progress =
                    (parseFloat(item.saleAmount as any) / maxSale) * 100;
                  const barColor = listColors[index % listColors.length];
                  return (
                    <li
                      key={`tech-${index}`}
                      style={{ width: "100%", marginBottom: "8px" }}
                    >
                      <Box className="flex items-center gap-2 mb-1">
                        <Box
                          sx={{
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: `${barColor}18`,
                            color: barColor,
                            fontFamily: "Saira, sans-serif",
                            fontSize: 10,
                            fontWeight: 800,
                          }}
                        >
                          {getInitials(item.email)}
                        </Box>
                        <Box className="flex-1 min-w-0">
                          <Box className="flex justify-between items-baseline gap-2">
                            <Tooltip followCursor title={item.email}>
                              <Typography
                                className="font-Saira truncate"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 700,
                                  color: "#334155",
                                }}
                              >
                                {item.email}
                              </Typography>
                            </Tooltip>
                            <Tooltip
                              followCursor
                              title={item?.saleAmount?.toLocaleString() || 0}
                            >
                              <Typography
                                className="font-Saira flex-shrink-0"
                                sx={{
                                  fontSize: 11,
                                  fontWeight: 800,
                                  color: barColor,
                                }}
                              >
                                {new Intl.NumberFormat("en", {
                                  notation: "compact",
                                  compactDisplay: "short",
                                  maximumFractionDigits: 2,
                                }).format(Math.abs(item?.saleAmount || 0))}
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: 9,
                                    fontWeight: 600,
                                    ml: 0.4,
                                    textTransform: "uppercase",
                                    color: "#94A3B8",
                                  }}
                                >
                                  {props?.user?.business?.currency || ""}
                                </Typography>
                              </Typography>
                            </Tooltip>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(Math.max(progress, 0), 100)}
                            sx={{
                              mt: 0.6,
                              height: 10,
                              borderRadius: 999,
                              backgroundColor: "#EEF2F7",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: barColor,
                                borderRadius: 999,
                              },
                            }}
                          />
                          <Box className="flex justify-between items-center mt-0.5">
                            <Typography
                              className="font-Saira truncate"
                              sx={{ fontSize: 9, color: "#94A3B8" }}
                            >
                              {item.phoneNo || ""}
                            </Typography>
                            <Typography
                              className="font-Saira"
                              sx={{ fontSize: 9, color: "#64748B" }}
                            >
                              {t("serviceCount")}:{" "}
                              <Box
                                component="span"
                                sx={{ fontWeight: 700, color: barColor }}
                              >
                                {item.serviceCount}
                              </Box>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </li>
                  );
                })
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
}
